import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Box,
  Button,
  Card,
  CardBody,
  Heading,
  HStack,
  IconButton,
  Spinner,
  Table,
  TableContainer,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tooltip,
  Tr,
  VStack,
  useBreakpointValue,
  useColorModeValue,
  useToast,
} from "@chakra-ui/react";
import { Link as RouterLink, useParams } from "react-router-dom";
import { FaEye, FaPencilAlt, FaPlus } from "react-icons/fa";
import { useUser } from "../../contexts/UserContext";
import { canManageNetwork } from "../../utils/authorization";
import PaginatedListToolbar from "../common/PaginatedListToolbar";
import PaginationControls from "../common/PaginationControls";
import usePaginatedList from "../common/usePaginatedList";
import { getNetworkOrganizations } from "./networkApi";

const formatLocation = (organization) =>
  [organization?.city, organization?.country].filter(Boolean).join(", ") || "Location not set";

const NetworkOrganizationsPage = () => {
  const { networkId } = useParams();
  const { user } = useUser();
  const toast = useToast();
  const isMobile = useBreakpointValue({ base: true, md: false });
  const cardBg = useColorModeValue("white", "gray.800");
  const borderColor = useColorModeValue("gray.200", "gray.700");
  const mutedColor = useColorModeValue("gray.600", "gray.400");

  const [organizations, setOrganizations] = useState([]);
  const [loading, setLoading] = useState(true);
  const canManageActiveNetwork = canManageNetwork(user, networkId);

  const loadOrganizations = useCallback(async () => {
    setLoading(true);
    try {
      setOrganizations(await getNetworkOrganizations(networkId));
    } catch (error) {
      console.error("Error loading network organizations:", error);
      toast({ title: "Could not load schools", status: "error", duration: 4000, isClosable: true });
      setOrganizations([]);
    } finally {
      setLoading(false);
    }
  }, [networkId, toast]);

  useEffect(() => {
    loadOrganizations();
  }, [loadOrganizations]);

  const sortOptions = useMemo(() => [
    { key: "name", label: "Name", getSortValue: (organization) => organization?.name },
    { key: "location", label: "Location", getSortValue: formatLocation },
  ], []);

  const {
    paginatedItems,
    totalCount,
    totalPages,
    currentPage,
    pageSize,
    searchText,
    sortKey,
    sortDir,
    setCurrentPage,
    setPageSize,
    setSearchText,
    setSortDir,
    setSortKey,
  } = usePaginatedList({
    items: organizations,
    sortOptions,
    initialSortKey: "name",
    initialSortDir: "asc",
    initialPageSize: 25,
    searchMatcher: (organization, normalizedSearch) =>
      [organization?.name, organization?.city, organization?.country, organization?.email]
        .some((value) => value?.toString().toLowerCase().includes(normalizedSearch)),
  });

  const renderActions = (organization, size = "xs") => (
    <HStack spacing={2} flexWrap="wrap">
      <Tooltip label="View school" hasArrow>
        <IconButton
          as={RouterLink}
          to={`/app/network/${networkId}/organizations/${organization.id}`}
          icon={<FaEye />}
          aria-label="View school"
          title="View school"
          variant="outline"
          size={size}
          colorScheme="cyan"
        />
      </Tooltip>
      {canManageActiveNetwork ? (
        <Tooltip label="Edit school" hasArrow>
          <IconButton
            as={RouterLink}
            to={`/app/network/${networkId}/organizations/${organization.id}/edit`}
            icon={<FaPencilAlt />}
            aria-label="Edit school"
            title="Edit school"
            variant="outline"
            size={size}
            colorScheme="yellow"
          />
        </Tooltip>
      ) : null}
    </HStack>
  );

  return (
    <VStack align="stretch" spacing={4}>
      <HStack justify="space-between" align={{ base: "start", md: "center" }} flexWrap="wrap">
        <Box>
          <Heading size="lg">Schools</Heading>
          <Text color={mutedColor} mt={1}>Schools associated with this network.</Text>
        </Box>
        {canManageActiveNetwork ? (
          <Button
            as={RouterLink}
            to={`/app/network/${networkId}/organizations/new`}
            leftIcon={<FaPlus />}
            colorScheme="blue"
            variant="outline"
            size="sm"
          >
            Add school
          </Button>
        ) : null}
      </HStack>

      <Card bg={cardBg} borderWidth="1px" borderColor={borderColor}>
        <CardBody>
          <PaginatedListToolbar
            searchText={searchText}
            onSearchTextChange={setSearchText}
            searchPlaceholder="Search schools"
            pageSize={pageSize}
            onPageSizeChange={setPageSize}
            totalCount={totalCount}
            sortKey={sortKey}
            sortDir={sortDir}
            sortOptions={sortOptions}
            onSortKeyChange={setSortKey}
            onSortDirChange={setSortDir}
          />
        </CardBody>
      </Card>

      {loading ? (
        <HStack justify="center" py={12}><Spinner size="lg" /></HStack>
      ) : totalCount === 0 ? (
        <Card bg={cardBg} borderWidth="1px" borderColor={borderColor}>
          <CardBody><Text color={mutedColor}>No schools found.</Text></CardBody>
        </Card>
      ) : isMobile ? (
        <VStack align="stretch" spacing={3}>
          {paginatedItems.map((organization) => (
            <Card key={organization.id} bg={cardBg} borderWidth="1px" borderColor={borderColor}>
              <CardBody>
                <VStack align="stretch" spacing={3}>
                  <Box>
                    <Text fontWeight="semibold">{organization.name || "Unnamed school"}</Text>
                    <Text fontSize="sm" color={mutedColor}>{formatLocation(organization)}</Text>
                  </Box>
                  {renderActions(organization, "sm")}
                </VStack>
              </CardBody>
            </Card>
          ))}
        </VStack>
      ) : (
        <Card bg={cardBg} borderWidth="1px" borderColor={borderColor}>
          <CardBody>
            <TableContainer>
              <Table>
                <Thead>
                  <Tr>
                    <Th>Name</Th>
                    <Th>Location</Th>
                    <Th>Actions</Th>
                  </Tr>
                </Thead>
                <Tbody>
                  {paginatedItems.map((organization) => (
                    <Tr key={organization.id}>
                      <Td fontWeight="semibold">{organization.name || "Unnamed school"}</Td>
                      <Td>{formatLocation(organization)}</Td>
                      <Td>
                        {renderActions(organization)}
                      </Td>
                    </Tr>
                  ))}
                </Tbody>
              </Table>
            </TableContainer>
          </CardBody>
        </Card>
      )}

      {!loading && totalCount > 0 ? (
        <PaginationControls currentPage={currentPage} totalPages={totalPages} onPageChange={setCurrentPage} />
      ) : null}
    </VStack>
  );
};

export default NetworkOrganizationsPage;
